/**
 * "Is this variant priced?" — one answer for every screen that would
 * otherwise read `variant.pricing[0]` and silently work with ₹0.
 *
 * A SKU can now exist with a barcode and NO usable price row (tagged at
 * creation, priced later at the first GRN). Every price row also carries
 * numeric columns that default to 0, so "has a pricing row" is not the same
 * as "has a price". These helpers look for a row that actually carries the
 * price the screen is about to use.
 */

type PricingRowLike = {
  sellingPrice?: number | string | null;
  mrp?: number | string | null;
  costPrice?: number | string | null;
  isActive?: boolean | null;
};

type VariantLike<T extends PricingRowLike> =
  | { pricing?: T[] | null }
  | null
  | undefined;

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** First pricing row (in the order the API returned them) with a positive selling price. */
export function sellingPriceRowOf<T extends PricingRowLike = any>(
  variant: VariantLike<T>,
): T | null {
  return (variant?.pricing ?? []).find((r) => num(r?.sellingPrice) > 0) ?? null;
}

/** First pricing row with a positive cost price or MRP — what a purchase document needs. */
export function purchasePriceRowOf<T extends PricingRowLike = any>(
  variant: VariantLike<T>,
): T | null {
  return (
    (variant?.pricing ?? []).find(
      (r) => num(r?.costPrice) > 0 || num(r?.mrp) > 0,
    ) ?? null
  );
}

export function hasSellingPrice(variant: VariantLike<PricingRowLike>): boolean {
  return !!sellingPriceRowOf(variant);
}

export function hasPurchasePrice(variant: VariantLike<PricingRowLike>): boolean {
  return !!purchasePriceRowOf(variant);
}

/** Shared wording so the cashier and the GRN operator see the same message. */
export function priceNotSetMessage(sku?: string | null, name?: string | null): string {
  const label = [sku, name].filter(Boolean).join(' - ') || 'this product';
  return `Price not set for ${label}. Add a price before using it.`;
}
